import PropTypes from 'prop-types';
import { useState, useRef, useEffect } from 'react';
// material
import { visuallyHidden } from '@material-ui/utils';
import { Box, Checkbox, TableRow, TableCell, TableHead, TableSortLabel, Tooltip } from '@material-ui/core';

const headCellSx = {
  color: 'text.secondary',
  '& .MuiTableSortLabel-root': {
    color: 'text.secondary'
  },
  '& .MuiTableSortLabel-root.Mui-active': {
    color: 'text.primary'
  },
  '& .MuiTableSortLabel-root .MuiTableSortLabel-icon': {
    color: 'text.secondary'
  }
};
import { SwapVert } from '@material-ui/icons';

// ----------------------------------------------------------------------

UserListHead.propTypes = {
  order: PropTypes.oneOf(['asc', 'desc']),
  orderBy: PropTypes.string,
  rowCount: PropTypes.number,
  headLabel: PropTypes.array,
  numSelected: PropTypes.number,
  onRequestSort: PropTypes.func,
  onSelectAllClick: PropTypes.func,
  columnWidths: PropTypes.object,
  onColumnResize: PropTypes.func,
  showSeparators: PropTypes.bool,
  onHeaderReorder: PropTypes.func
};

export default function UserListHead({
  order,
  orderBy,
  rowCount,
  headLabel,
  numSelected,
  onRequestSort,
  onSelectAllClick,
  showAllCheckBox,
  showIdLabel,
  showId,
  checkboxPresent=false,
  columnWidths = {},
  onColumnResize,
  showSeparators = false,
  onHeaderReorder,
  noSort=false
}) {
  const [resizingColumn, setResizingColumn] = useState(null);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const resizeRefs = useRef({});

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const handleMouseDown = (e, columnId) => {
    e.preventDefault();
    e.stopPropagation();
    const cell = resizeRefs.current[columnId];
    if (!cell) return;
    
    const rect = cell.getBoundingClientRect();
    setResizingColumn(columnId);
    setStartX(e.clientX);
    setStartWidth(columnWidths[columnId] || rect.width);
  };

  useEffect(() => {
    if (!resizingColumn || !onColumnResize) return;

    const handleMouseMove = (e) => {
      const diff = e.clientX - startX;
      const newWidth = Math.max(30, startWidth + diff); // Minimum width 50px
      onColumnResize(resizingColumn, newWidth);
    };

    const handleMouseUp = () => {
      setResizingColumn(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingColumn, startX, startWidth, onColumnResize]);

  const handleDragStart = (e, index) => {
    if (!onHeaderReorder) return;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', index);
  };

  const handleDragOver = (e, index) => {
    if (!onHeaderReorder || draggedIndex === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    if (!onHeaderReorder || draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }
    e.preventDefault();
    onHeaderReorder(draggedIndex, dropIndex);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <TableHead>
      <TableRow>
      {(!checkboxPresent)&&
      <TableCell 
        padding="checkbox" 
        align={showIdLabel ? 'center' : 'center'}
        sortDirection={!noSort && showId && (orderBy === showId ? showId : false)}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: 'background.paper',
          ...headCellSx
        }}
      >
      {showAllCheckBox &&
          <Checkbox
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
          />
         }
         {showIdLabel && (
          noSort ? (
            showIdLabel
          ) : (
            <TableSortLabel
              // hideSortIcon
              active={orderBy === showId}
              direction={orderBy === showId ? order : 'asc'}
              onClick={createSortHandler(showId)}
              IconComponent={orderBy === showId ? undefined : SwapVert}
            >
              {showIdLabel}
              {orderBy === showId ? (
                  <Box sx={{ ...visuallyHidden }}>{order === 'desc' ? 'sorted descending' : 'sorted ascending'}</Box>
                ) : null}
            </TableSortLabel>
          )
         )}
         </TableCell>}
        {headLabel.map((headCell, index) => {
          const isLastColumn = index === headLabel.length - 1;
          return (
          <TableCell
            key={headCell.id}
            ref={(el) => {
              if (el && headCell.id) {
                resizeRefs.current[headCell.id] = el;
              }
            }}
            align={headCell.alignRight ? 'right' : headCell.alignLeft ? 'left' : 'center'}
            sortDirection={noSort ? false : (orderBy === headCell.id ? order : false)}
            draggable={onHeaderReorder && headCell.id !== 'options' && headCell.id !== ''}
            onDragStart={onHeaderReorder && headCell.id !== 'options' && headCell.id !== '' ? (e) => handleDragStart(e, index) : undefined}
            onDragOver={onHeaderReorder && headCell.id !== 'options' && headCell.id !== '' ? (e) => handleDragOver(e, index) : undefined}
            onDragLeave={onHeaderReorder && headCell.id !== 'options' && headCell.id !== '' ? handleDragLeave : undefined}
            onDrop={onHeaderReorder && headCell.id !== 'options' && headCell.id !== '' ? (e) => handleDrop(e, index) : undefined}
            onDragEnd={onHeaderReorder && headCell.id !== 'options' && headCell.id !== '' ? handleDragEnd : undefined}
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              width: columnWidths[headCell.id] || 'auto',
              minWidth: columnWidths[headCell.id] || 'auto',
              maxWidth: columnWidths[headCell.id] || 'none',
              userSelect: resizingColumn === headCell.id ? 'none' : 'auto',
              cursor: onHeaderReorder && headCell.id !== 'options' && headCell.id !== '' ? (draggedIndex === index ? 'grabbing' : 'grab') : 'default',
              opacity: draggedIndex === index ? 0.5 : 1,
              backgroundColor: dragOverIndex === index ? 'action.hover' : 'background.paper',
              transition: 'background-color 0.2s, opacity 0.2s',
              ...headCellSx,
              ...(showSeparators && !isLastColumn && {
                boxShadow: (theme) => `inset -1px 0 0 ${theme.palette.divider}`,
              })
            }}
          >
            {noSort ? (
              headCell?.tooltip ? (
                <Tooltip title={headCell.tooltip} arrow>
                  <span>{headCell.label}</span>
                </Tooltip>
              ) : (
                headCell.label
              )
            ) : (
              (headCell.disableSort || !headCell?.label || headCell?.label?.trim() == 'Options' || headCell?.label?.trim() == 'qb' || headCell?.label?.trim() == 'Qb' || headCell?.label?.trim() === '') ? (
                headCell.label
              ) : (
                headCell?.tooltip ? (
                  <Tooltip title={headCell.tooltip} arrow>
                    <TableSortLabel
                      // hideSortIcon
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={createSortHandler(headCell.id)}
                      IconComponent={
                        orderBy === headCell.id
                          ? undefined
                          : SwapVert
                      }
                    >
                      {headCell.label}
                      {orderBy === headCell.id ? (
                        <Box sx={{ ...visuallyHidden }}>{order === 'desc' ? 'sorted descending' : 'sorted ascending'}</Box>
                      ) : null}
                    </TableSortLabel>
                  </Tooltip>
                ) : (
                  <TableSortLabel
                    // hideSortIcon
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : 'asc'}
                    onClick={createSortHandler(headCell.id)}
                    IconComponent={
                      orderBy === headCell.id
                        ? undefined
                        : SwapVert
                    }
                  >
                    {headCell?.tooltip ? (
                      <Tooltip title={headCell?.tooltip} >
                        <span>{headCell.label}</span>
                      </Tooltip>
                    ) : (
                      headCell.label
                    )}
                    {orderBy === headCell.id ? (
                      <Box sx={{ ...visuallyHidden }}>{order === 'desc' ? 'sorted descending' : 'sorted ascending'}</Box>
                    ) : null}
                  </TableSortLabel>
                )
              )
            )}
            {headCell.id && headCell.id !== 'options' && onColumnResize && (
              <Box
                onMouseDown={(e) => handleMouseDown(e, headCell.id)}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  position: 'absolute',
                  right: '-3px',
                  top: 0,
                  bottom: 0,
                  width: '6px',
                  cursor: 'col-resize',
                  backgroundColor: resizingColumn === headCell.id ? 'primary.main' : 'transparent',
                  borderRight: '1px solid',
                  borderColor: resizingColumn === headCell.id ? 'primary.main' : 'transparent',
                  '&:hover': {
                    backgroundColor: resizingColumn === headCell.id ? 'primary.main' : 'rgba(0, 0, 0, 0.1)',
                    borderColor: 'primary.main'
                  },
                  zIndex: 11,
                  transition: 'background-color 0.2s, border-color 0.2s'
                }}
              />
            )}
          </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}
